import { FormEvent, useEffect, useState } from 'react'
import { useI18n } from '../i18n'
import type { MessageKey } from '../i18n'
import { listJournal, type JournalEntry } from '../api/journal'
import {
  fetchLedgerReview, fetchLedgerSettings, fetchLedgerStatus, postFedWatchReading, saveLedgerReview, saveLedgerSettings,
  type FedWatchResult, type LedgerCondition, type LedgerReview, type LedgerSettings, type LedgerSettingsResponse,
  type LedgerStatus, type StressScenario,
} from '../api/ledger'

type Tab = 'settings' | 'today' | 'calc' | 'review'

// Every editable number is addressed by its dotted path into `settings`, so the
// same string keys drive the form, the i18n label/helper lookup and the
// `missing` banner without a second mapping table.
const STEP1 = ['account.loss_budget_pct', 'account.drawdown_warn_pct', 'account.drawdown_pause_pct', 'account.equity_high_water']
const MARGIN = ['margin.l_cap', 'margin.cost_pct', 'margin.maintenance_buffer_pct', 'margin.profit_trigger_pct', 'margin.post_profit_l_target', 'margin.post_profit_loss_budget_pct']
const STEP2 = ['account.exposure_cap_e', 'account.single_name_cap_pct', 'account.sector_cap_pct']
const FW_INFLATION = ['framework.inflation.pass_max', 'framework.inflation.fail_above']
const FW_ENERGY = ['framework.energy.pass_max', 'framework.energy.warn_above', 'framework.energy.fail_above']
const FW_POLICY = ['framework.policy.pass_max_net_hikes', 'framework.policy.fail_above_net_hikes', 'framework.policy.reading_ttl_days']
const FW_RATES = ['framework.rates.warn_5d_bp', 'framework.rates.fail_no_retrace_days', 'framework.rates.retrace_target_pct']

const TEXT_PATHS = new Set(['framework.inflation.metric', 'framework.inflation.consecutive', 'framework.policy.target_meeting'])

const REVIEW_QUESTIONS = ['scenario_judgement', 'rate_shock', 'profit_mgmt', 'account_risk', 'exec_cost', 'vs_baseline'] as const

type Draft = Record<string, string>

function flatten(node: unknown, prefix: string, out: Draft) {
  if (node == null || typeof node !== 'object' || Array.isArray(node)) return
  for (const [k, v] of Object.entries(node as Record<string, unknown>)) {
    const path = prefix ? `${prefix}.${k}` : k
    if (typeof v === 'number' || typeof v === 'string') out[path] = String(v)
    else flatten(v, path, out)
  }
}

function assemble(draft: Draft, scenarios: StressScenario[], marginEnabled: boolean, universe: string): LedgerSettings {
  const out: Record<string, unknown> = {}
  for (const [path, raw] of Object.entries(draft)) {
    const value = raw.trim()
    if (value === '') continue
    const parts = path.split('.')
    let node = out
    for (const part of parts.slice(0, -1)) {
      node[part] = node[part] || {}
      node = node[part] as Record<string, unknown>
    }
    const leaf = parts[parts.length - 1]
    node[leaf] = TEXT_PATHS.has(path) && Number.isNaN(Number(value)) ? value : Number(value)
  }
  const s = out as LedgerSettings
  // §3.4 fixes the energy metric to WTI against the latest CPI stat month; only
  // the thresholds are the user's to write.
  if (s.framework?.energy) s.framework.energy.metric = 'wti_vs_cpi_month_pct'
  const symbols = universe.split(/[,\s]+/).map((x) => x.trim().toUpperCase()).filter(Boolean)
  if (symbols.length) s.framework = { ...s.framework, earnings: { universe: symbols } }
  if (scenarios.length) s.stress = { scenarios }
  s.margin = { ...s.margin, enabled: marginEnabled }
  return s
}

function NumField({ path, draft, set }: { path: string; draft: Draft; set: (path: string, value: string) => void }) {
  const { t } = useI18n()
  return (
    <label className="ledger-field">
      <span>{t(`ledger.f.${path}` as MessageKey)}</span>
      <input type="number" step="any" value={draft[path] ?? ''} onChange={(e) => set(path, e.target.value)} />
      <small className="muted">{t(`ledger.h.${path}` as MessageKey)}</small>
    </label>
  )
}

function SettingsForm({ conf, onSaved }: { conf: LedgerSettingsResponse; onSaved: (next: LedgerSettingsResponse) => void }) {
  const { t } = useI18n()
  const [draft, setDraft] = useState<Draft>(() => { const d: Draft = {}; flatten(conf.settings, '', d); return d })
  const [scenarios, setScenarios] = useState<StressScenario[]>(conf.settings.stress?.scenarios || [])
  const [marginEnabled, setMarginEnabled] = useState(conf.settings.margin?.enabled === true)
  const [marginOpen, setMarginOpen] = useState(conf.settings.margin?.enabled === true)
  const [universe, setUniverse] = useState((conf.settings.framework?.earnings?.universe || []).join(', '))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const set = (path: string, value: string) => setDraft((prev) => ({ ...prev, [path]: value }))

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError('')
    try {
      onSaved(await saveLedgerSettings({ settings: assemble(draft, scenarios, marginEnabled, universe) }))
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <form className="ledger-settings" onSubmit={submit}>
      <section className="ledger-step">
        <h3>{t('ledger.step1')}</h3>
        <div className="ledger-grid">{STEP1.map((p) => <NumField key={p} path={p} draft={draft} set={set} />)}</div>
        <div className="ledger-margin">
          <button type="button" className="ledger-margin__head" onClick={() => setMarginOpen((v) => !v)}>
            <span>{t('ledger.margin.title')}</span>
            <span aria-hidden>{marginOpen ? '▾' : '▸'}</span>
          </button>
          {marginOpen && (
            <>
              <label className="ledger-toggle">
                <input type="checkbox" checked={marginEnabled} onChange={(e) => setMarginEnabled(e.target.checked)} />
                <span>{t('ledger.margin.enable')}</span>
              </label>
              <p className="muted small">{t('ledger.margin.help')}</p>
              {marginEnabled && <div className="ledger-grid">{MARGIN.map((p) => <NumField key={p} path={p} draft={draft} set={set} />)}</div>}
            </>
          )}
        </div>
      </section>

      <section className="ledger-step">
        <h3>{t('ledger.step2')}</h3>
        <div className="ledger-grid">{STEP2.map((p) => <NumField key={p} path={p} draft={draft} set={set} />)}</div>
        <h4 className="ledger-sub">{t('ledger.stress.title')}</h4>
        <p className="muted small">{t('ledger.stress.help')}</p>
        {scenarios.map((sc, i) => (
          <div key={i} className="ledger-scenario">
            <input
              value={sc.name}
              placeholder={t('ledger.stress.name')}
              onChange={(e) => setScenarios(scenarios.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
            />
            <input
              type="number"
              step="any"
              value={sc.index_drop_pct ?? ''}
              onChange={(e) => setScenarios(scenarios.map((x, j) => (
                j === i ? { ...x, index_drop_pct: e.target.value.trim() === '' ? undefined : Number(e.target.value) } : x
              )))}
            />
            <span className="muted small">{t('ledger.stress.drop')}</span>
            <button type="button" className="button button--ghost button--small" onClick={() => setScenarios(scenarios.filter((_, j) => j !== i))}>
              {t('ledger.stress.remove')}
            </button>
          </div>
        ))}
        <button type="button" className="button button--secondary button--small" onClick={() => setScenarios([...scenarios, { name: '' }])}>
          {t('ledger.stress.add')}
        </button>
      </section>

      <section className="ledger-step">
        <h3>{t('ledger.step3')}</h3>

        <h4 className="ledger-sub">{t('ledger.cond.inflation')}</h4>
        <div className="ledger-grid">
          <label className="ledger-field">
            <span>{t('ledger.f.framework.inflation.metric')}</span>
            <select value={draft['framework.inflation.metric'] ?? ''} onChange={(e) => set('framework.inflation.metric', e.target.value)}>
              <option value="">{t('ledger.choose')}</option>
              <option value="core_cpi_mom">{t('ledger.metric.coreCpi')}</option>
              <option value="cpi_mom">{t('ledger.metric.cpi')}</option>
            </select>
            <small className="muted">{t('ledger.h.framework.inflation.metric')}</small>
          </label>
          {FW_INFLATION.map((p) => <NumField key={p} path={p} draft={draft} set={set} />)}
          <label className="ledger-field">
            <span>{t('ledger.f.framework.inflation.consecutive')}</span>
            <select value={draft['framework.inflation.consecutive'] ?? ''} onChange={(e) => set('framework.inflation.consecutive', e.target.value)}>
              <option value="">{t('ledger.choose')}</option>
              <option value="1">{t('ledger.consecutive.one')}</option>
              <option value="2">{t('ledger.consecutive.two')}</option>
            </select>
            <small className="muted">{t('ledger.h.framework.inflation.consecutive')}</small>
          </label>
        </div>

        <h4 className="ledger-sub">{t('ledger.cond.energy')}</h4>
        <p className="muted small">{t('ledger.energy.metricNote')}</p>
        <div className="ledger-grid">{FW_ENERGY.map((p) => <NumField key={p} path={p} draft={draft} set={set} />)}</div>

        <h4 className="ledger-sub">{t('ledger.cond.policy')}</h4>
        <div className="ledger-grid">
          <label className="ledger-field">
            <span>{t('ledger.f.framework.policy.target_meeting')}</span>
            <input type="date" value={draft['framework.policy.target_meeting'] ?? ''} onChange={(e) => set('framework.policy.target_meeting', e.target.value)} />
            <small className="muted">{t('ledger.h.framework.policy.target_meeting')}</small>
          </label>
          {FW_POLICY.map((p) => <NumField key={p} path={p} draft={draft} set={set} />)}
        </div>

        <h4 className="ledger-sub">{t('ledger.cond.rates')}</h4>
        <div className="ledger-grid">{FW_RATES.map((p) => <NumField key={p} path={p} draft={draft} set={set} />)}</div>

        <h4 className="ledger-sub">{t('ledger.cond.earnings')}</h4>
        <label className="ledger-field ledger-field--wide">
          <span>{t('ledger.f.framework.earnings.universe')}</span>
          <input value={universe} onChange={(e) => setUniverse(e.target.value)} placeholder="AAPL, MSFT" />
          <small className="muted">{t('ledger.h.framework.earnings.universe')}</small>
        </label>
      </section>

      {error && <p className="form-message form-message--error">{error}</p>}
      <button type="submit" className="button button--primary" disabled={saving}>{saving ? t('ledger.saving') : t('ledger.save')}</button>
    </form>
  )
}

function num(v?: number | null, digits = 2): string {
  return v == null ? '—' : v.toFixed(digits)
}

function pct(v?: number | null): string {
  return v == null ? '—' : `${(v * 100).toFixed(1)}%`
}

function money(v?: number | null): string {
  return v == null ? '—' : `$${Math.round(v).toLocaleString()}`
}

function ConditionRow({ c, onRecord }: { c: LedgerCondition; onRecord: () => void }) {
  const { t } = useI18n()
  const badge = c.state === 'pass' ? 'green' : c.state === 'fail' ? 'rose' : 'amber'
  return (
    <li className="ledger-cond">
      <span className={`pill pill--${badge}`}>{t(`ledger.state.${c.state}` as MessageKey)}</span>
      <span className="ledger-cond__label">{t(`ledger.cond.${c.key}` as MessageKey) || c.key}</span>
      {c.value != null && <span className="badge badge--muted">{typeof c.value === 'number' ? c.value.toFixed(2) : c.value}</span>}
      {c.invalidated && <span className="pill pill--rose">{t('ledger.state.invalidated')}</span>}
      <span className="muted small ledger-cond__why">{c.why}</span>
      {c.needs_input && (
        <button type="button" className="button button--ghost button--small" onClick={onRecord}>{t('ledger.today.record')}</button>
      )}
    </li>
  )
}

function TodayCards({ status, onRecord }: { status: LedgerStatus; onRecord: () => void }) {
  const { t } = useI18n()
  const a = status.account_card
  const f = status.framework_card
  const dateLine = status.as_of ? t('events.asOf').replace('{d}', status.as_of.slice(0, 10)) : null

  return (
    <div className="ledger-today">
      <div className="ledger-card">
        <h3>{t('ledger.today.accountCard')}</h3>
        {dateLine && <p className="muted small">{dateLine}</p>}
        <div className="ledger-metrics">
          <span><strong>{num(a.e)}</strong><small>{t('ledger.today.e')}</small></span>
          <span><strong>{num(a.l)}</strong><small>{t('ledger.today.l')}</small></span>
          <span><strong>{money(a.n)}</strong><small>{t('ledger.today.n')}</small></span>
          <span><strong>{pct(a.drawdown_pct)}</strong><small>{t('ledger.today.drawdown')}</small></span>
          {a.margin_call_drop_pct != null && (
            <span><strong>{pct(a.margin_call_drop_pct)}</strong><small>{t('ledger.today.marginCall')}</small></span>
          )}
        </div>
        {a.exposure_by_bucket && (
          <p className="muted small">
            {t('ledger.today.bucketCore')} {money(a.exposure_by_bucket.core)} · {t('ledger.today.bucketEvent')} {money(a.exposure_by_bucket.event)}
          </p>
        )}
        {a.long_call_count > 0 && (
          <p className="muted small">
            {t('ledger.today.longCall').replace('{v}', money(a.long_call_premium)).replace('{n}', String(a.long_call_count))}
            <br />
            {t('ledger.today.longCallNote')}
          </p>
        )}
        <h4 className="ledger-sub">{t('ledger.today.stress')}</h4>
        {(a.stress || []).length === 0 ? (
          <p className="muted small">{t('ledger.today.noStress')}</p>
        ) : (
          <ul className="ledger-rows">
            {a.stress.map((s) => (
              <li key={s.name}>
                <span>{s.name}</span>
                <span>{money(s.loss)}</span>
                <span className="muted small">{t('ledger.today.budget')} {money(s.budget)}</span>
                {s.breach && <span className="pill pill--rose">{t('ledger.today.breach')}</span>}
              </li>
            ))}
          </ul>
        )}
        <h4 className="ledger-sub">{t('ledger.today.triggers')}</h4>
        {(a.triggers || []).length === 0 ? (
          <p className="muted small">{t('ledger.today.noTriggers')}</p>
        ) : (
          <ul className="ledger-rows">
            {a.triggers.map((g) => (
              <li key={g.key}>
                <span className={`pill pill--${g.breached ? 'rose' : 'green'}`}>
                  {t(g.breached ? 'ledger.today.triggerBreached' : 'ledger.today.triggerOk')}
                </span>
                <span className="muted small">{g.why}</span>
              </li>
            ))}
          </ul>
        )}
        {a.partial && a.partial.length > 0 && (
          <p className="muted small">{t('ledger.today.partial').replace('{syms}', a.partial.join(', '))}</p>
        )}
        <p className="ledger-footer">{t('ledger.footer')}</p>
      </div>

      <div className="ledger-card">
        <h3>{t('ledger.today.frameworkCard')}</h3>
        {dateLine && <p className="muted small">{dateLine}</p>}
        <p className="ledger-scenario-label">
          <span className={`pill pill--${f.scenario === 'A' ? 'green' : f.scenario === 'C' ? 'rose' : 'amber'}`}>{f.scenario}</span>
          <strong>{t(`ledger.scenario.${f.scenario}` as MessageKey)}</strong>
        </p>
        <ul className="ledger-conds">
          {(f.conditions || []).map((c) => <ConditionRow key={c.key} c={c} onRecord={onRecord} />)}
        </ul>
        {f.downgrade_at && <p className="muted small">{t('ledger.today.downgradeAt')} {f.downgrade_at}</p>}
        {f.upgrade_requires && <p className="muted small">{t('ledger.today.upgradeRequires')} {f.upgrade_requires}</p>}
        <p className="ledger-footer">{t('ledger.footer')}</p>
      </div>
    </div>
  )
}

function FedWatchCalculator({ ttlDays, meeting }: { ttlDays?: number; meeting?: string }) {
  const { t } = useI18n()
  const today = new Date().toISOString().slice(0, 10)
  const defaultValid = ttlDays != null
    ? new Date(Date.now() + ttlDays * 86400000).toISOString().slice(0, 10)
    : today
  const [form, setForm] = useState({ meeting: meeting || '', m0: '', view: '', valid_until: defaultValid })
  const [rows, setRows] = useState([{ midpoint: '', prob: '' }])
  const [result, setResult] = useState<FedWatchResult | null>(null)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError('')
    try {
      setResult(await postFedWatchReading({
        meeting: form.meeting,
        m0: Number(form.m0),
        outcomes: rows.filter((r) => r.midpoint.trim() && r.prob.trim()).map((r) => ({ midpoint: Number(r.midpoint), prob: Number(r.prob) })),
        ...(form.view.trim() ? { view: form.view.trim() } : {}),
      }, form.valid_until))
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <form className="ledger-card" onSubmit={submit}>
      <h3>{t('ledger.calc.fedwatch.title')}</h3>
      <div className="ledger-grid">
        <label className="ledger-field">
          <span>{t('ledger.calc.fedwatch.meeting')}</span>
          <input type="date" required value={form.meeting} onChange={(e) => setForm({ ...form, meeting: e.target.value })} />
          <small className="muted">{t('ledger.calc.fedwatch.meeting.help')}</small>
        </label>
        <label className="ledger-field">
          <span>{t('ledger.calc.fedwatch.m0')}</span>
          <input type="number" step="any" required value={form.m0} onChange={(e) => setForm({ ...form, m0: e.target.value })} />
          <small className="muted">{t('ledger.calc.fedwatch.m0.help')}</small>
        </label>
        <label className="ledger-field">
          <span>{t('ledger.calc.fedwatch.validUntil')}</span>
          <input type="date" required value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
          <small className="muted">{t('ledger.calc.fedwatch.validUntil.help')}</small>
        </label>
      </div>

      <h4 className="ledger-sub">{t('ledger.calc.fedwatch.outcomes')}</h4>
      <p className="muted small">{t('ledger.calc.fedwatch.outcomes.help')}</p>
      {rows.map((r, i) => (
        <div key={i} className="ledger-scenario">
          <input
            type="number" step="any" value={r.midpoint}
            onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, midpoint: e.target.value } : x)))}
          />
          <span className="muted small">{t('ledger.calc.fedwatch.midpoint')}</span>
          <input
            type="number" step="any" value={r.prob}
            onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, prob: e.target.value } : x)))}
          />
          <span className="muted small">{t('ledger.calc.fedwatch.prob')}</span>
        </div>
      ))}
      {rows.length < 6 && (
        <button type="button" className="button button--secondary button--small" onClick={() => setRows([...rows, { midpoint: '', prob: '' }])}>
          {t('ledger.calc.fedwatch.addRow')}
        </button>
      )}

      <label className="ledger-field ledger-field--wide">
        <span>{t('ledger.calc.fedwatch.view')}</span>
        <input value={form.view} onChange={(e) => setForm({ ...form, view: e.target.value.slice(0, 280) })} maxLength={280} />
        <small className="muted">{t('ledger.calc.fedwatch.view.help')}</small>
      </label>

      {error && <p className="form-message form-message--error">{error}</p>}
      <button type="submit" className="button button--primary" disabled={saving}>{saving ? t('ledger.saving') : t('ledger.calc.fedwatch.submit')}</button>

      {result && (
        <div className="ledger-metrics ledger-metrics--result">
          <span><strong>{result.mu_t.toFixed(3)}</strong><small>{t('ledger.calc.fedwatch.muT')}</small></span>
          <span><strong>{result.delta_r.toFixed(3)}</strong><small>{t('ledger.calc.fedwatch.deltaR')}</small></span>
          <span><strong>{result.net_hikes.toFixed(2)}</strong><small>{t('ledger.calc.fedwatch.netHikes')}</small></span>
          <span>
            <strong>{result.change_vs_prev?.comparable ? result.change_vs_prev.net_hikes.toFixed(2) : '—'}</strong>
            <small>{result.change_vs_prev?.comparable ? t('ledger.calc.fedwatch.changeVsPrev') : t('ledger.calc.fedwatch.notComparable')}</small>
          </span>
        </div>
      )}
    </form>
  )
}

function StressCalculator({ status }: { status: LedgerStatus | null }) {
  const { t } = useI18n()
  const [drop, setDrop] = useState('')
  const holdings = status?.account_card.holdings || []
  const dropPct = Number(drop)
  const usable = holdings.length > 0 && drop.trim() !== '' && Number.isFinite(dropPct)
  const betaAssumed = holdings.some((h) => h.beta_assumed)
  const rows = holdings.map((h) => ({ ...h, loss: h.market_value * h.beta * (dropPct / 100) }))
  const total = rows.reduce((sum, r) => sum + r.loss, 0)

  return (
    <div className="ledger-card">
      <h3>{t('ledger.calc.stress.title')}</h3>
      {status?.as_of && <p className="muted small">{t('events.asOf').replace('{d}', status.as_of.slice(0, 10))}</p>}
      <label className="ledger-field ledger-field--wide">
        <span>{t('ledger.calc.stress.indexDrop')}</span>
        <input type="number" step="any" value={drop} onChange={(e) => setDrop(e.target.value)} />
        <small className="muted">{t('ledger.calc.stress.indexDrop.help')}</small>
      </label>
      {holdings.length === 0 ? (
        <p className="muted small">{t('ledger.calc.stress.noHoldings')}</p>
      ) : (
        <>
          {betaAssumed && <p className="muted small">{t('ledger.calc.stress.betaAssumed')}</p>}
          <ul className="ledger-rows">
            {rows.map((r) => (
              <li key={r.symbol}>
                <span><strong>{r.symbol}</strong></span>
                <span>{money(r.market_value)}</span>
                <span className="muted small">β {r.beta.toFixed(2)}</span>
                <span>{usable ? money(r.loss) : '—'}</span>
              </li>
            ))}
          </ul>
          <p className="ledger-total">{t('ledger.calc.stress.total')} <strong>{usable ? money(total) : '—'}</strong></p>
        </>
      )}
    </div>
  )
}

function ReviewForm({ eventId, entries }: { eventId: string; entries: JournalEntry[] }) {
  const { t } = useI18n()
  const [review, setReview] = useState<LedgerReview>({ answers: {}, judged_correct: null })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    // A cycle that has never been reviewed has no stored row; an empty form is
    // the correct starting state for it.
    fetchLedgerReview(eventId).then(setReview).catch(() => setReview({ answers: {}, judged_correct: null }))
  }, [eventId])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError('')
    setSaved(false)
    try {
      await saveLedgerReview(eventId, review)
      setSaved(true)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <form className="ledger-card" onSubmit={submit}>
      <h3>{eventId}</h3>
      <ul className="ledger-rows">
        {entries.map((e) => (
          <li key={e.id}>
            <span>{e.symbol || '—'}</span>
            <span className="badge">{t(`journal.action.${e.action}` as MessageKey) || e.action}</span>
            <span className="muted small">{e.reason}</span>
            <span className="muted small">{e.created_at.slice(0, 10)}</span>
          </li>
        ))}
      </ul>
      {REVIEW_QUESTIONS.map((q) => (
        <label key={q} className="ledger-field ledger-field--wide">
          <span>{t(`ledger.review.q.${q}` as MessageKey)}</span>
          <textarea
            rows={2}
            value={review.answers[q] || ''}
            onChange={(ev) => setReview({ ...review, answers: { ...review.answers, [q]: ev.target.value.slice(0, 1000) } })}
          />
        </label>
      ))}
      <div className="ledger-tristate">
        <span>{t('ledger.review.judged')}</span>
        {([true, false, null] as const).map((v) => (
          <button
            key={String(v)}
            type="button"
            className={`button button--small ${review.judged_correct === v ? 'button--primary' : 'button--ghost'}`}
            onClick={() => setReview({ ...review, judged_correct: v })}
          >
            {t(v === true ? 'ledger.review.yes' : v === false ? 'ledger.review.no' : 'ledger.review.unknown')}
          </button>
        ))}
      </div>
      {error && <p className="form-message form-message--error">{error}</p>}
      <button type="submit" className="button button--primary" disabled={saving}>{saving ? t('ledger.saving') : t('ledger.review.save')}</button>
      {saved && <p className="muted small">{t('ledger.review.saved')}</p>}
    </form>
  )
}

function ReviewTab() {
  const { t } = useI18n()
  const [entries, setEntries] = useState<JournalEntry[]>([])

  useEffect(() => {
    listJournal(50).then((r) => setEntries(r.items || [])).catch(() => setEntries([]))
  }, [])

  const settled = entries.filter((e) => e.event_id && e.settlement)
  const byEvent = new Map<string, JournalEntry[]>()
  for (const e of settled) byEvent.set(e.event_id!, [...(byEvent.get(e.event_id!) || []), e])

  if (byEvent.size === 0) return <p className="muted">{t('ledger.review.empty')}</p>
  return (
    <div className="ledger-reviews">
      {Array.from(byEvent.entries()).map(([eventId, items]) => (
        <ReviewForm key={eventId} eventId={eventId} entries={items} />
      ))}
    </div>
  )
}

export function LedgerPage() {
  const { language, t } = useI18n()
  const [conf, setConf] = useState<LedgerSettingsResponse | null>(null)
  const [status, setStatus] = useState<LedgerStatus | null>(null)
  const [tab, setTab] = useState<Tab>('today')
  const [error, setError] = useState('')
  const [enabling, setEnabling] = useState(false)

  useEffect(() => {
    fetchLedgerSettings().then(setConf).catch((err: Error) => setError(err.message))
  }, [])

  useEffect(() => {
    if (!conf?.enabled || !conf.complete) { setStatus(null); return }
    fetchLedgerStatus().then(setStatus).catch((err: Error) => setError(err.message))
  }, [conf?.enabled, conf?.complete, language])

  const enable = async () => {
    setEnabling(true)
    setError('')
    try {
      setConf(await saveLedgerSettings({ enabled: true }))
      setTab('settings')
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setEnabling(false)
    }
  }

  const head = (
    <header className="scanner__head">
      <div>
        <p className="kicker">{t('ledger.kicker')}</p>
        <h1>{t('ledger.title')}</h1>
        <p className="muted">{t('ledger.subtitle')}</p>
      </div>
    </header>
  )

  if (!conf) {
    return <section className="scanner">{head}{error && <p className="form-message form-message--error">{error}</p>}</section>
  }

  if (!conf.enabled) {
    return (
      <section className="scanner">
        {head}
        {error && <p className="form-message form-message--error">{error}</p>}
        <div className="ledger-card">
          <p>{t('ledger.off.body')}</p>
          <button type="button" className="button button--primary" onClick={enable} disabled={enabling}>
            {enabling ? t('ledger.saving') : t('ledger.off.enable')}
          </button>
        </div>
      </section>
    )
  }

  const tabs: Tab[] = ['today', 'settings', 'calc', 'review']

  return (
    <section className="scanner">
      {head}
      {error && <p className="form-message form-message--error">{error}</p>}

      {!conf.complete && (
        <div className="form-message form-message--warn">
          <p>{t('ledger.missing.title')}</p>
          <p>{conf.missing.map((k) => t(`ledger.f.${k.replace(/\[\d+\]/g, '')}` as MessageKey) || k).join(' · ')}</p>
        </div>
      )}

      <nav className="ledger-tabs">
        {tabs.map((x) => (
          <button key={x} type="button" className={tab === x ? 'is-active' : undefined} onClick={() => setTab(x)}>
            {t(`ledger.tab.${x}` as MessageKey)}
          </button>
        ))}
      </nav>

      {tab === 'settings' && <SettingsForm conf={conf} onSaved={setConf} />}

      {tab === 'today' && (
        !conf.complete ? <p className="form-message form-message--warn">{t('ledger.readonly')}</p>
          : status ? <TodayCards status={status} onRecord={() => setTab('calc')} />
            : <p className="muted">{t('ledger.today.loading')}</p>
      )}

      {tab === 'calc' && (
        <div className="ledger-today">
          <FedWatchCalculator
            ttlDays={conf.settings.framework?.policy?.reading_ttl_days}
            meeting={conf.settings.framework?.policy?.target_meeting}
          />
          <StressCalculator status={status} />
        </div>
      )}

      {tab === 'review' && <ReviewTab />}
    </section>
  )
}
